import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { BulleAide, Button, Question, SmartButton } from '@workspace/core-ts/components';
import { normalizeEuro } from '@workspace/utils';
import { useEffect, useRef, useState } from 'react';
import { useMutationFill } from '../../api';
import { attentePaiementStepContent } from '../../content';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '../../contexts';
import { useContent, useSource } from '../../hooks';
import type { StepContentConfig } from '../../utils';

interface AttentePaiementStepProps {
    content?: StepContentConfig;
    refreshInterval?: number;
}

export function AttentePaiementStep({ content = attentePaiementStepContent, refreshInterval = 60 }: AttentePaiementStepProps) {
    const methods = useSmartFormGlobal<any>();
    const { meta, actions, others, setDisableNavigation } = useGlobalForm();
    const mutationFill = useMutationFill(meta.token);
    const r = useContent();
    const { isCourtier, isConseiller } = useSource();

    const [countdown, setCountdown] = useState(refreshInterval);
    const intervalRef = useRef<number | null>(null);
    const countdownIntervalRef = useRef<number | null>(null);

    const hasPayments = others.payments?.length > 0;
    const lastPaiement = others.payments?.[others.payments.length - 1] || {};

    const resetIntervals = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

        intervalRef.current = setInterval(handleFill, refreshInterval * 1000);
        countdownIntervalRef.current = setInterval(() => {
            setCountdown((current) => (current > 0 ? current - 1 : refreshInterval));
        }, 1000);
    };

    useEffect(() => {
        if (hasPayments && !lastPaiement.paid) {
            // Permettre aux conseillers/courtiers de revenir en arrière pour modifier le devis
            // (ex: corriger l'email avant de renvoyer le lien de paiement)
            if (!isCourtier && !isConseiller) {
                setDisableNavigation(true);
            }
            resetIntervals();
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPayments, lastPaiement.paid, setDisableNavigation, isCourtier, isConseiller]);

    const handleFill = async () => {
        try {
            const response = await mutationFill.mutateAsync(methods.getValues());
            actions.saveData(response);
            setCountdown(refreshInterval);

            // Si paiement effectué, réinitialiser les intervalles
            if (!response.payments[response.payments.length - 1].paid) {
                resetIntervals();
            }
        } catch (error) {
            console.error('Erreur mise à jour paiement:', error);
        }
    };

    const handleSubmit = async (data: any) => {
        if (lastPaiement.paid) {
            return actions.goNextQuestion();
        }
        const response = await mutationFill.mutateAsync(data);
        return response?.data;
    };

    const getTarifs = () => {
        const Devis = methods.watch('Devis');
        const fractionnement = methods.watch('DemandeTarif.Police.FractionnementChoisi') || 'M';

        if (!Devis?.Formule?.MontantComptantPromo) return null;

        return {
            premierPaiement: Devis.Formule.MontantComptantPromo,
            fractionnement,
        };
    };

    const tarifs = getTarifs();

    return (
        <SmartFormGlobal
            {...methods}
            className="mx-auto max-w-2xl mt-6 sm:mt-10 mb-20 padding-root"
            onSubmit={handleSubmit}
        >
            <Question.Header>
                <ClockIcon className="w-12 h-12 text-primary mx-auto mb-4" />
                <Question.Title>{r(content.mainTitle)}</Question.Title>
            </Question.Header>

            {(isConseiller || isCourtier) && !lastPaiement.paid && (
                <BulleAide
                    className="w-full max-w-md mx-auto mb-6"
                    description={r(content.lienPaiementEnvoye)}
                />
            )}

            {tarifs && (
                <div className="w-full max-w-md mx-auto mb-6">
                    <div className="rounded-xl p-6 text-center">
                        <p className="text-sm text-neutral-600 mb-2">Montant réglé</p>
                        <p className="text-3xl font-bold text-primary">{normalizeEuro(tarifs.premierPaiement)}</p>
                    </div>
                </div>
            )}

            <div className="space-y-4 max-w-md w-full">
                {lastPaiement.paid ? (
                    <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <CheckCircleIcon className="w-6 h-6 text-success-500" />
                                <h3 className="font-medium text-success-700">{r(content.paiementEffectue)}</h3>
                            </div>
                            {lastPaiement?.transaction_date && (
                                <p className="text-sm text-success-600">{new Date(lastPaiement.transaction_date).toLocaleString('fr-FR')}</p>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        {lastPaiement?.history && lastPaiement.history.length > 0 ? (
                            <div className="flex flex-col w-full bg-card divide-y-4 divide-white rounded-md">
                                {lastPaiement.history.map((h: any, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-4"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-sm">{h.date ? '🟢' : '🔴'}</span>
                                            <h3>{h.name}</h3>
                                        </div>
                                        <p className="text-secondary text-sm">{h.date ? new Date(h.date).toLocaleString('fr-FR') : 'En attente'}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                                <div className="flex items-center gap-3">
                                    <ClockIcon className="w-6 h-6 text-warning-500" />
                                    <h3 className="font-medium text-warning-700">{r(content.paiementEnAttente)}</h3>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {!lastPaiement.paid && (
                <>
                    <Button
                        arrow={false}
                        loading={mutationFill.isPending}
                        onClick={handleFill}
                        className="w-full"
                    >
                        {r(content.buttonUpdate)} ({countdown}s)
                    </Button>
                    <p className="text-center text-xs text-neutral-500">
                        {r(content.lastUpdate)} {new Date(others.updated_at).toLocaleString('fr-FR')}
                    </p>
                </>
            )}

            {lastPaiement.paid && <SmartButton />}
        </SmartFormGlobal>
    );
}
